/**
 * Per-pilot route / area preference briefs — soft bias only.
 * Company Charter brief remains the hard/company-level doctrine.
 * Empty prefs = no forced pick.
 */
import { destRegionOf, hopNm, type DestRegion } from "./inboxMintMemory";
import { normalizeIcao } from "./airportDoctrine";
import type { CharterRequest, CrewMember, SceneryAirport } from "./types";

export const PREFERRED_TRIP_NM_MIN = 150;
export const PREFERRED_TRIP_NM_MAX = 2200;
/** Soft band around a target average when min/max are unset. */
export const PREFERRED_TRIP_NM_BAND = 280;

export type CrewRoutePrefs = {
  routeAreaPrefs: string;
  preferredTripNm: number | null;
};

const ICAO_TOKEN = /\b[A-Z]{4}\b/g;

const REGION_HINTS: Array<{ region: DestRegion; keys: string[] }> = [
  { region: "maritimes", keys: ["maritim", "cape breton", "nova scotia", "cabot", "atlantic canada", "peel", "newfoundland", "labrador"] },
  { region: "quebec", keys: ["quebec", "montr", "montreal", "st-hubert", "ottawa"] },
  { region: "ontario", keys: ["ontario", "toronto", "buttonville", "hamilton", "billy bishop"] },
  { region: "prairies", keys: ["prairie", "winnipeg", "saskatoon", "regina", "calgary", "edmonton"] },
  { region: "west", keys: ["west coast", "vancouver", "victoria", "kelowna"] },
  { region: "us_ne", keys: ["new england", "teterboro", "white plains", "bedford", "boston", "northeast", "new york"] },
  { region: "us_fl", keys: ["florida", "palm beach", "miami", "fort lauderdale", "boca", "naples"] },
  { region: "us_mid", keys: ["mid-atlantic", "dulles", "philadelphia", "charlotte"] },
  { region: "caribbean", keys: ["caribbean", "bahamas", "nassau", "bermuda", "san juan"] },
  { region: "mexico", keys: ["mexico", "cancun", "toluca"] },
];

export function hydratePreferredTripNm(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(PREFERRED_TRIP_NM_MAX, Math.max(PREFERRED_TRIP_NM_MIN, Math.round(n)));
}

export function hydrateCrewRoutePrefs(raw?: Partial<CrewRoutePrefs> | CrewMember | null): CrewRoutePrefs {
  const rec = raw && typeof raw === "object" ? raw : {};
  const text =
    typeof (rec as CrewRoutePrefs).routeAreaPrefs === "string"
      ? (rec as CrewRoutePrefs).routeAreaPrefs
      : "";
  return {
    routeAreaPrefs: text.trim().slice(0, 800),
    preferredTripNm: hydratePreferredTripNm((rec as CrewRoutePrefs).preferredTripNm),
  };
}

export function prefsIcaosFromText(text: string): string[] {
  const hits = text.toUpperCase().match(ICAO_TOKEN) ?? [];
  return [...new Set(hits.map((icao) => normalizeIcao(icao)).filter(Boolean))];
}

export function prefsRegionsFromText(text: string): DestRegion[] {
  const blob = text.toLowerCase();
  if (!blob.trim()) return [];
  return REGION_HINTS.filter((row) => row.keys.some((key) => blob.includes(key))).map((row) => row.region);
}

export function distancePrefDelta(nm: number | null | undefined, targetNm: number | null): number {
  if (nm == null || !Number.isFinite(nm) || targetNm == null) return 0;
  const delta = Math.abs(nm - targetNm);
  if (delta <= PREFERRED_TRIP_NM_BAND * 0.45) return 10;
  if (delta <= PREFERRED_TRIP_NM_BAND) return 5;
  if (delta <= PREFERRED_TRIP_NM_BAND * 2) return -4;
  return -10;
}

export type CrewPrefMatchInput = {
  originIcao: string;
  destIcao: string;
  destName?: string;
  nm?: number | null;
  scenery?: SceneryAirport[];
};

/** Soft score. 0 when prefs are empty. Never a hard reject. */
export function scoreCrewPrefMatch(prefs: CrewRoutePrefs, rfq: CrewPrefMatchInput): number {
  const text = prefs.routeAreaPrefs.trim();
  const target = prefs.preferredTripNm;
  if (!text && target == null) return 0;
  let score = 0;
  const dest = normalizeIcao(rfq.destIcao);
  const origin = normalizeIcao(rfq.originIcao);
  const nm = rfq.nm ?? hopNm(origin, dest, rfq.scenery ?? []);
  if (text) {
    const icaos = prefsIcaosFromText(text);
    if (icaos.includes(dest) || icaos.includes(origin)) score += 12;
    const regions = prefsRegionsFromText(text);
    const destRegion = destRegionOf(dest);
    if (regions.includes(destRegion)) score += 8;
    const blob = text.toLowerCase();
    const destName = (rfq.destName ?? "").toLowerCase();
    if (destName && blob.includes(destName.split(",")[0] ?? destName)) score += 4;
  }
  score += distancePrefDelta(nm, target);
  return score;
}

export function unionCrewPrefHints(crew: ReadonlyArray<Partial<CrewMember> | CrewRoutePrefs>): {
  texts: string[];
  targetNms: number[];
  icaos: string[];
} {
  const texts: string[] = [];
  const targetNms: number[] = [];
  const icaos = new Set<string>();
  for (const row of crew) {
    const prefs = hydrateCrewRoutePrefs(row);
    if (prefs.routeAreaPrefs) {
      texts.push(prefs.routeAreaPrefs);
      for (const icao of prefsIcaosFromText(prefs.routeAreaPrefs)) icaos.add(icao);
    }
    if (prefs.preferredTripNm != null) targetNms.push(prefs.preferredTripNm);
  }
  return { texts, targetNms, icaos: [...icaos] };
}

export function formatCrewPrefMintPrompt(crew: ReadonlyArray<Partial<CrewMember> | CrewRoutePrefs>): string {
  const union = unionCrewPrefHints(crew);
  if (!union.texts.length && !union.targetNms.length) return "";
  const bits = [
    "Crew route prefs (soft hints only — company Charter brief + home-origin doctrine still win; never invent ICAOs):",
  ];
  if (union.texts.length) {
    bits.push(`Union of pilot area/route notes: ${union.texts.map((t) => t.slice(0, 160)).join(" · ")}.`);
  }
  if (union.targetNms.length) {
    const avg = Math.round(union.targetNms.reduce((s, n) => s + n, 0) / union.targetNms.length);
    bits.push(`Soft-prefer a mix near crew target trip distances (~${union.targetNms.join("/")} NM, union ~${avg} NM). Empty/unset distance = no bias.`);
  }
  bits.push("Overlap or empty prefs = no forced dest. Diversify using the union, not one pilot’s East Coast taste.");
  return bits.join("\n");
}

export function formatCrewPrefAssignPrompt(): string {
  return "When choosing among legal company pilots, read snapshot.charterOpsBrief first (hard). Then soft-bias toward a pilot whose routeAreaPrefs / preferredTripNm match the RFQ. Overlap or empty prefs = no forced pick.";
}

export function requestPrefContext(
  request: Pick<CharterRequest, "originIcao" | "destIcao" | "destName">,
  scenery: SceneryAirport[] = [],
): CrewPrefMatchInput {
  return {
    originIcao: request.originIcao,
    destIcao: request.destIcao,
    destName: request.destName,
    nm: hopNm(request.originIcao, request.destIcao, scenery),
    scenery,
  };
}
